import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';
import { useI18n } from 'vue-i18n';
import '../contracts/MessagingAnalytics_UnansweredByDayResponse';
import '../contracts/MessagingAnalytics_DashboardSummaryResponse';
import '../contracts/MessagingAnalytics_ChartDataResponse';
const C = 'useMessagingAnalytics';

// Local response shape — endpoints are served by the Go messaging-service and
// have no PHP ResponseContract. Kept minimal: only the keys the consumers read.
interface MessagingAnalyticsEnvelope {
  success: boolean;
  dados?: Record<string, unknown>;
  data?: Record<string, unknown>;
  erro?: string;
  error?: string;
}

interface MessagingDashboardSummary {
  totais?: Record<string, number>;
  por_dispositivo?: Array<Record<string, unknown>>;
  [key: string]: unknown;
}

interface MessagingChartData {
  [key: string]: unknown;
}

interface MessagingUnansweredPorDiaPoint {
  data: string;
  chats_sem_resposta?: number;
  chats_nao_lidos?: number;
}

interface MessagingUnansweredDevice {
  device_name: string;
  por_dia?: MessagingUnansweredPorDiaPoint[];
}

interface MessagingUnansweredByDay {
  por_dia?: MessagingUnansweredPorDiaPoint[];
  por_dispositivo?: MessagingUnansweredDevice[];
}

export function useMessagingAnalytics() {
  const { t } = useI18n();

  const summaryData = ref<MessagingDashboardSummary | null>(null);
  const chartData = ref<MessagingChartData | null>(null);
  const unansweredByDayData = ref<MessagingUnansweredByDay | null>(null);
  const isLoading = ref(false);
  const isLoadingChart = ref(false);
  const isLoadingUnanswered = ref(false);
  const error = ref(null);

  log(C, 'init', 'composable created');

  const fetchDashboardSummary = async () => {
    log(C, 'fetchDashboardSummary', 'called');
    isLoading.value = true;
    error.value = null;
    try {
      const url = `/index.php/config/messaging_analytics/dashboard_summary`;
      log(C, 'fetchDashboardSummary', 'URL:', url);

      const response = await httpService.get<MessagingAnalyticsEnvelope>(url, { responseContract: 'MessagingAnalytics_DashboardSummaryResponse' });
      log(C, 'fetchDashboardSummary', 'RAW response:', JSON.stringify(response).substring(0, 1000));
      log(C, 'fetchDashboardSummary', 'response keys:', response ? Object.keys(response) : 'null');
      log(C, 'fetchDashboardSummary', 'response.success:', response.success, 'response.success:', response.success);

      if (response.success) {
        const data = (response.dados || response.data) as MessagingDashboardSummary | undefined;
        log(C, 'fetchDashboardSummary', 'SUCCESS - data keys:', data ? Object.keys(data) : 'null');
        log(C, 'fetchDashboardSummary', 'totais:', JSON.stringify(data?.totais));
        log(C, 'fetchDashboardSummary', 'por_dispositivo count:', data?.por_dispositivo?.length);
        log(C, 'fetchDashboardSummary', 'por_dispositivo sample:', JSON.stringify(data?.por_dispositivo?.[0]));
        summaryData.value = data ?? null;
        return summaryData.value;
      } else {
        logError(C, 'fetchDashboardSummary', 'FAILED response:', JSON.stringify(response));
        throw new Error(response.erro || response.error || t('Erro ao carregar dados'));
      }
    } catch (err) {
      error.value = err.message;
      logError(C, 'fetchDashboardSummary', 'ERROR:', err.message);
      logError(C, 'fetchDashboardSummary', 'ERROR stack:', err.stack);
      throw err;
    } finally {
      isLoading.value = false;
      log(C, 'fetchDashboardSummary', 'FINALLY - isLoading:', isLoading.value, 'error:', error.value, 'hasData:', !!summaryData.value);
    }
  };

  const fetchChartData = async (chartType = 'volume') => {
    log(C, 'fetchChartData', 'called, chartType:', chartType);
    isLoadingChart.value = true;
    try {
      const url = `/index.php/config/messaging_analytics/chart_data/${chartType}`;
      log(C, 'fetchChartData', 'URL:', url);

      const response = await httpService.get<MessagingAnalyticsEnvelope>(url, { responseContract: 'MessagingAnalytics_ChartDataResponse' });
      log(C, 'fetchChartData', 'RAW response:', JSON.stringify(response).substring(0, 1000));
      log(C, 'fetchChartData', 'response keys:', response ? Object.keys(response) : 'null');

      if (response.success) {
        chartData.value = (response.dados || response.data) as MessagingChartData | null;
        log(C, 'fetchChartData', 'SUCCESS - data keys:', chartData.value ? Object.keys(chartData.value) : 'null');
        return chartData.value;
      } else {
        logError(C, 'fetchChartData', 'FAILED:', JSON.stringify(response));
        throw new Error(response.erro || response.error || t('Erro ao carregar dados do grafico'));
      }
    } catch (err) {
      logError(C, 'fetchChartData', 'ERROR:', err.message, 'stack:', err.stack);
      throw err;
    } finally {
      isLoadingChart.value = false;
      log(C, 'fetchChartData', 'FINALLY - isLoadingChart:', isLoadingChart.value, 'hasData:', !!chartData.value);
    }
  };

  const fetchUnansweredByDay = async () => {
    log(C, 'fetchUnansweredByDay', 'called');
    isLoadingUnanswered.value = true;
    try {
      const url = `/index.php/config/messaging_analytics/unanswered_by_day`;
      const response = await httpService.get<MessagingAnalyticsEnvelope>(url, {
        responseContract: 'MessagingAnalytics_UnansweredByDayResponse'
      });

      if (response.success) {
        unansweredByDayData.value = (response.dados || response.data) as MessagingUnansweredByDay | null;
        log(C, 'fetchUnansweredByDay', 'SUCCESS - days:', unansweredByDayData.value?.por_dia?.length);
        return unansweredByDayData.value;
      } else {
        logError(C, 'fetchUnansweredByDay', 'FAILED:', JSON.stringify(response));
        throw new Error(response.erro || response.error || t('Erro ao carregar dados'));
      }
    } catch (err) {
      logError(C, 'fetchUnansweredByDay', 'ERROR:', err.message);
      throw err;
    } finally {
      isLoadingUnanswered.value = false;
    }
  };

  const getScoreColor = (score: any) => {
    if (score >= 80) return 'green';
    if (score >= 60) return 'orange';
    return 'red';
  };

  const getScoreBadge = (score: any) => {
    if (score >= 80) return t('Excelente');
    if (score >= 60) return t('Bom');
    return t('Precisa Melhorar');
  };

  const getScoreSeverity = (score: any) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warn';
    return 'danger';
  };

  const getAwaitingReplySeverity = (count: any) => {
    if (count === 0) return 'success';
    if (count <= 5) return 'warn';
    return 'danger';
  };

  return {
    summaryData,
    chartData,
    unansweredByDayData,
    isLoading,
    isLoadingChart,
    isLoadingUnanswered,
    error,
    fetchDashboardSummary,
    fetchChartData,
    fetchUnansweredByDay,
    getScoreColor,
    getScoreBadge,
    getScoreSeverity,
    getAwaitingReplySeverity
  };
}
